import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import cs from 'app/components/Sidebar/styles.module.scss';

/** Italic placeholder for a field the user hasn't filled in. */
export const SidebarEmptyValue: React.FC = () => <i className={cs.emptyValue}>None</i>;

/** Small muted paragraph explaining a control or a download format. */
export const SidebarParagraph: React.FC<React.PropsWithChildren<{className?: string}>> = ({
  className,
  children,
}) => <p className={classnames(cs.paragraph, className)}>{children}</p>;

export interface SidebarFieldGroupProps {
  /** Width of the label column for the inline rows inside. */
  labelWidth?: string;
  className?: string;
}

/** Stack of field rows sharing one label-column width. */
export const SidebarFieldGroup: React.FC<React.PropsWithChildren<SidebarFieldGroupProps>> = ({
  labelWidth,
  className,
  children,
}) => (
  <div
    className={classnames(cs.fieldGroup, className)}
    style={
      labelWidth ? ({'--sidebarFieldLabelWidth': labelWidth} as React.CSSProperties) : undefined
    }
  >
    {children}
  </div>
);

export interface SidebarFieldRowProps {
  label: React.ReactNode;
  /** Control next to the label, typically an info link. */
  labelAction?: React.ReactNode;
  value?: React.ReactNode;
  /**
   * `auto` keeps the label in a fixed-width leading column while the value fits
   * beside it and drops the value onto its own full-width line when it doesn't.
   * `stacked` always puts the value on its own line.
   */
  layout?: 'auto' | 'stacked';
  /** Prompts for a new value on click, revealing an edit button on hover. */
  onEdit?: () => void;
  /** Text put on the clipboard by the copy button. */
  copyValue?: string;
  /**
   * Control at the trailing edge, revealed with the edit and copy buttons. For
   * edit controls that need their own trigger, like a menu.
   */
  trailingAction?: React.ReactNode;
  truncate?: boolean;
  className?: string;
}

/** Label/value row. Edit and copy stay hidden until the row is hovered or focused. */
export const SidebarFieldRow: React.FC<React.PropsWithChildren<SidebarFieldRowProps>> = ({
  label,
  labelAction,
  value,
  layout = 'auto',
  onEdit,
  copyValue,
  trailingAction,
  truncate,
  className,
  children,
}) => {
  const lowercaseLabel = typeof label === 'string' ? label.toLocaleLowerCase() : '';

  return (
    <div
      className={classnames(
        cs.fieldRow,
        {
          [cs.fieldRowAuto]: layout === 'auto',
          [cs.fieldRowStacked]: layout === 'stacked',
        },
        className
      )}
    >
      <div className={cs.fieldLabel}>
        {label}
        {labelAction && <span className={cs.labelAction}>{labelAction}</span>}
      </div>
      <div className={cs.fieldValueWrapper}>
        <div
          className={classnames(cs.fieldValue, {[cs.fieldValueTruncated]: truncate})}
          // A clipped value is unreadable at rest, so let hover show the whole thing.
          title={truncate ? copyValue : undefined}
        >
          {value ?? children}
        </div>
        {(onEdit || copyValue || trailingAction) && (
          <div className={cs.hoverActions}>
            {onEdit && (
              <B.Tooltip content={`Edit ${lowercaseLabel}`}>
                <B.Button icon="edit" minimal small onClick={onEdit} />
              </B.Tooltip>
            )}
            {copyValue && (
              <B.Tooltip content="Copy to clipboard">
                <B.Button
                  icon="clipboard"
                  minimal
                  small
                  onClick={() => navigator.clipboard.writeText(copyValue)}
                />
              </B.Tooltip>
            )}
            {trailingAction}
          </div>
        )}
      </div>
    </div>
  );
};
